import {
  Button,
  FormGroup,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  IconButton,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import "./style.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import AxiosInstance from "../../utils/axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import backimg from "../../assets/image/icons/back.png";
import InputText from "../InputFields/InputText";
import SearchInput from "../InputFields/SearchInput";
import AppLogo from "../../assets/image/CMS_LOGO.svg";
import showToast from "../Toast/Toster";
import { useUserType } from "../../contexts/UserTypeContext";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { OrangeButton } from "../Icon/Index";
import { useRecaptchaV3 } from "../../hooks/useRecaptchaV3";

const RECAPTCHA_ACTION_FORGOT_PASSWORD = "forgot_password";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailCheckData, setEmailCheckData] = useState(null);
  const { executeRecaptcha, isReady: recaptchaReady, error: recaptchaError, resetError: resetRecaptchaError } = useRecaptchaV3();

  const [selectedRole, setSelectedRole] = useState("");
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState("");
  const { userType, logo } = useUserType();
  const baseUrl = import.meta.env.VITE_BASE_API;

  const formik = useFormik({
    initialValues: {
      EmailAddress: "",
    },
    validationSchema: Yup.object().shape({
      EmailAddress: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
    }),
    onSubmit: (values) => {
      if (!showRoleSelection) {
        resetRecaptchaError();
        if (!recaptchaReady) {
          showToast.error("Security check is loading. Please try again in a moment.");
          return;
        }
        executeRecaptcha(RECAPTCHA_ACTION_FORGOT_PASSWORD)
          .then((token) => {
            handleCheckEmail(values.EmailAddress, token);
          })
          .catch(() => {
            showToast.error("Security verification failed. Please try again.");
          });
        return;
      } else {
        // Find the selected company data to get both UserId and Role
        const selectedCompany = emailCheckData?.companies?.find(
          (company) => (company.UserId || company.Id) === selectedRole
        );
        if (selectedCompany) {
          const userId = selectedCompany.UserId || selectedCompany.Id;
          const role = selectedCompany.Role;
          handleSendMail(values.EmailAddress, userId, role);
        }
      }
    },
  });

  const handleCheckEmail = async (email, recaptchaToken) => {
    setIsCheckingEmail(true);
    try {
      const payload = { EmailAddress: email };
      if (recaptchaToken) payload.recaptchaToken = recaptchaToken;
      const res = await AxiosInstance.post(`/v1/auth/check-email`, payload);

      if (res?.data?.statusCode === "200" && res?.data?.data) {
        
        const data = res?.data?.data;
        // Handle different response structures
        let companiesToShow = [];
        if (data?.Role === "Admin") {
          companiesToShow.push(data);
        } else if (data?.Role) {
          companiesToShow.push(data);
        } else if (data?.companies) {
          companiesToShow = data.companies;
        } else if (Array.isArray(data)) {
          companiesToShow = data;
        }
        // Check if multiple companies/roles exist
        if (companiesToShow.length > 1) {
          setEmailCheckData({
            ...data,
            companies: companiesToShow,
            multipleCompanies: true,
          });
          setAccountSearchQuery("");
          setShowRoleSelection(true);
          showToast.success("Email found! Please select your role below.", {
            position: "top-center",
            autoClose: 2000,
          });
        } else if (companiesToShow.length === 1) {
          // Single role found, proceed directly
          const singleCompany = companiesToShow[0];
          const userIdValue =
            singleCompany.UserId || singleCompany.Id || "null";
          const roleValue = singleCompany.Role || "null";
          setSelectedRole(roleValue);
          handleSendMail(email, userIdValue, roleValue);
        } else {
          showToast.error("Email not found in the system");
        }
      } else if (res?.data?.statusCode === "201") {
        showToast.error("Email not found");
      } else {
        showToast.error("Email not found");
      }
    } catch (error) {
      console.error("Email check error:", error);
      showToast.error(
        error?.response?.data?.message || "We couldn't verify that email address. Please try again in a moment."
      );
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleSendMail = async (email, userId, role) => {
    setIsLoading(true);
    try {
      const data = {
        EmailAddress: email,
        Role: role,
        UserId: userId,
      };
      const res = await AxiosInstance.post(
        `/v1/forget-pass/resetpasswordmail`,
        data
      );
      if (res?.status === 200 && res?.data?.statusCode === 200) {
        showToast.success("Mail sent successfully", {
          position: "top-center",
          autoClose: 1000,
        });
        setTimeout(() => {
          navigate(`/auth/login`);
        }, 2000);
      } else if (res?.data?.statusCode === 209) {
        const reason = res?.data?.reason;
        const message =
          reason === "email_disabled" || reason === "not_configured"
            ? "Email is disabled for this company. Please contact support."
            : (res?.data?.message || "You have reached the email limit");
        showToast.warning(message, {
          position: "top-center",
        });
      } else {
        // Check if there's an error message in the response
        const errorMessage = res?.data?.message || "Email sending failed";
        console.error("API returned error:", errorMessage);
        showToast.error(errorMessage);
      }
    } catch (error) {
      if (error?.response?.status === 209 || error?.response?.data?.statusCode === 209) {
        const reason = error?.response?.data?.reason;
        const message =
          reason === "email_disabled" || reason === "not_configured"
            ? "Email is disabled for this company. Please contact support."
            : (error?.response?.data?.message || "You have reached the email limit");
        showToast.warning(message, {
          position: "top-center",
        });
      } else if (error?.response) {
        showToast.error(
          error?.response?.data?.message || "We couldn't send the reset email. Please try again in a moment."
        );
      } else if (error?.request) {
        showToast.error("No response from the server, please try again later.");
      } else {
        showToast.error("Error occurred while sending request.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const imageLoaded = sessionStorage.getItem("imageLoaded");

    if (imageLoaded === "true") {
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
    }
  }, []);

  const handleImageLoad = () => {
    setIsLoaded(true);
    sessionStorage.setItem("imageLoaded", "true");
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleBackToEmail = () => {
    setShowRoleSelection(false);
    setEmailCheckData(null);
    setSelectedRole("");
    setAccountSearchQuery("");
  };

  // Filter account list by search (same as login "Search company or customer...")
  const filteredAccounts = useMemo(() => {
    const companies = emailCheckData?.companies || [];
    if (!companies.length) return [];
    const q = (accountSearchQuery || "").toLowerCase().trim();
    if (!q) return companies;
    return companies.filter((company) => {
      const name = (company?.CompanyName || "").toLowerCase();
      const owner = (company?.OwnerName || "").toLowerCase();
      const first = (company?.FirstName || "").toLowerCase();
      const last = (company?.LastName || "").toLowerCase();
      const role = (company?.Role || "").toLowerCase();
      const tagLabel = company?.IsDonationMode ? "donation" : "job";
      const isCompanyRole = (company?.Role || "").toLowerCase() === "company";
      const searchableText = isCompanyRole
        ? `${name} ${owner} ${role} ${tagLabel}`
        : `${name} ${first} ${last} ${role} ${tagLabel}`;
      return searchableText.replace(/\s+/g, " ").trim().includes(q);
    });
  }, [emailCheckData?.companies, accountSearchQuery]);

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    formik.handleChange(event);
    
    // If email is cleared or changed, hide role selection
    if (!newEmail || newEmail !== formik.initialValues.EmailAddress) {
      setShowRoleSelection(false);
      setEmailCheckData(null);
      setSelectedRole("");
    }
  };
  return (
    <Grid className="login">
      <Grid container spacing={0} className="vh-100" style={{ margin: 0, padding: 0 }}>
        <Grid
          className="d-flex justify-content-center  align-items-center pt-0 forgatePasswordNoneHe forgotTopGive"
          size={{
            xs: 12,
            md: 6
          }}>
          <Grid style={{width: "100%",display: "flex",flexDirection: "column",alignItems: "center"}}>
          <Typography
            className="app-logo forgotpassWordImageSet"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* JOB-3155: inherit `.app-logo` sizing (~65px) from Login screen instead of the oversized 500px inline width. */}
            <img src={logo} alt="logo" style={{ marginBottom: "20px" }} />
          </Typography>
          <Grid
            style={{
              width: "80%",
            }}
            className="d-flex flex-direction-column align-items-center p-2 maon-forget-password"
          >
            <form
              onSubmit={formik?.handleSubmit}
              className="d-flex flex-column justify-content-between forgoFormSubmitClass"
              style={{ height: "60%",width: "100%" }}
            >
              <Typography
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  textAlign: "left",
                  marginTop: "13px",
                  display: "flex",
                  cursor: "pointer",
                  alignItems: "center",
                }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                <ChevronLeftIcon className={userType === "donor" ? "text-donor-color" : "text-orange-color"} style={{ fontSize: "30px" }} />
                <span className="mx-2" style={{ fontSize: "16px" }}>Go back to <span className={userType === "donor" ? "text-donor-color" : "text-orange-color"}>Log In</span></span>
              </Typography>

              <Grid>
                <Typography
                  className="text forgotHeadText text-blue-color"
                  style={{ fontSize: "35px", marginTop: "20px" ,marginBottom: "20px"}}
                >
                  Forgot your password?
                </Typography>
                {/* <Typography className="text2 " style={{ marginTop: "20px" }}>
                  Don't worry, it happens to all of us. Enter your email below
                  to recover your password.
                </Typography> */}
                <FormGroup style={{ width: "100%", marginTop: "20px" }}>
                  <InputText
                    value={formik?.values?.EmailAddress}
                    onChange={handleEmailChange}
                    onBlur={formik?.handleBlur}
                    error={
                      formik?.touched?.EmailAddress &&
                      Boolean(formik?.errors?.EmailAddress)
                    }
                    helperText={
                      formik?.touched?.EmailAddress && formik?.errors?.EmailAddress
                    }
                    name="EmailAddress"
                    label="Email"
                    required
                    type="email"
                    className="text-blue-color w-100"
                    // fieldHeight="44px"
                    disabled={showRoleSelection}
                  />
                  {/* JOB-3368: render the inline validation error -- InputText
                      hides helper text when it has an error (project rule). */}
                  {formik?.touched?.EmailAddress && formik?.errors?.EmailAddress ? (
                    <div className="cs-field-helper cs-field-helper--error" role="alert">
                      {formik.errors.EmailAddress}
                    </div>
                  ) : null}
                </FormGroup>

                {/* <Box style={{ width: "100%", marginTop: "20px" }}>
                    <FormControl component="fieldset" style={{ width: "100%" }}>
                    {showRoleSelection && emailCheckData && (

                      <RadioGroup
                        value={selectedRole}
                        onChange={handleRoleChange}
                        style={{ marginTop: "10px", width: "100%" }}
                      >
                        {emailCheckData.companies.map((company, index) => {
                          const userId = company.UserId || company.Id || "null";
                          return (
                            <FormControlLabel
                              key={userId}
                              value={userId}
                              control={<Radio />}
                              label={
                                <Box style={{ width: "100%" }}>
                                  <Typography
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "500",
                                      color: "#333",
                                    }}
                                  >
                                    {company.CompanyName} ({company.IsDonationMode ? "Donor" : "Job"}) - {company.Role} ({company.Role === "Customer" 
                                      ? `${company.FirstName || 'Customer'} ${company.LastName || ''}`
                                      : company.Role === "Worker"
                                      ? `${company.FirstName || 'Worker'} ${company.LastName || ''}`
                                      : company.Role === "Company"
                                      ? `${company.OwnerName || company.CompanyName}`
                                      : company.CompanyName
                                    })
                                    
                                  </Typography>
                                </Box>
                              }
                              style={{
                                marginBottom: "8px",
                                padding: "8px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                                width: "100%",
                                backgroundColor:
                                  selectedRole === userId
                                    ? "#f5f5f5"
                                    : "white",
                                    marginLeft: "0px",
                              }}
                            />
                          );
                        })}
                        
                      </RadioGroup>
                )}

                    </FormControl>
                  </Box> */}

                {/* Role Selection - Show search + radio list when no role is selected (same as login account list) */}
                {showRoleSelection && emailCheckData && !selectedRole && (
                  <Box style={{ width: "100%" }}>
                    <FormControl component="fieldset" style={{ width: "100%" }}>
                      <Box>
                        <RadioGroup
                          value={selectedRole}
                          onChange={handleRoleChange}
                          style={{ marginTop: "10px", width: "100%" }}
                        >
                          <Box
                            className="role-selection-scroll"
                            style={{
                              maxHeight: window.innerWidth < 768 ? "300px" : "260px",
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              padding: window.innerWidth < 768 ? "8px" : "12px",
                              backgroundColor: "#fafafa",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                            }}
                            sx={{
                              "@media (max-width: 768px)": {
                                height: "300px",
                                maxHeight: "300px",
                                minHeight: "260px",
                                padding: "8px",
                              },
                              "@media (max-width: 480px)": {
                                height: "280px",
                                maxHeight: "280px",
                                minHeight: "240px",
                                padding: "6px",
                              },
                            }}
                          >
                            <SearchInput
                              placeholder="Search company or customer..."
                              value={accountSearchQuery}
                              onChange={(e) =>
                                setAccountSearchQuery(e.target.value?.trimStart())
                              }
                              fieldHeight="36px"
                              fullWidth
                              style={{
                                width: "100%",
                                marginBottom: "6px",
                                flexShrink: 0,
                              }}
                            />
                            <Box
                              style={{
                                overflowY: "auto",
                                flex: 1,
                                minHeight: 0,
                              }}
                            >
                            {filteredAccounts?.length === 0 ? (
                              <Typography
                                style={{
                                  fontSize: "14px",
                                  color: "#666",
                                  textAlign: "center",
                                  padding: "16px 0",
                                }}
                              >
                                {accountSearchQuery
                                  ? "No account matches your search."
                                  : "No accounts to show."}
                              </Typography>
                            ) : (
                            filteredAccounts?.map((company, index) => {
                              const userId =
                                company.UserId || company.Id || "null";
                              const isSelected = selectedRole === userId;
                              return (
                                <Box
                                  key={userId}
                                  className={`role-selection-card-compact ${isSelected ? 'selected' : ''}`}
                                  onClick={() => setSelectedRole(userId)}
                                >
                                  <FormControlLabel
                                    value={userId}
                                    control={
                                      <Radio 
                                        size="small"
                                        style={{ 
                                          color: isSelected ? "#2563eb" : "#666",
                                          marginRight: "8px"
                                        }} 
                                      />
                                    }
                                    label={
                                      <Box style={{ width: "100%" }}>
                                        <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                          <Typography
                                            style={{
                                              fontSize: window.innerWidth < 480 ? "13px" : "15px",
                                              fontWeight: "700",
                                              color: "#333",
                                              flex: 1,
                                            }}
                                            sx={{
                                              '@media (max-width: 480px)': {
                                                fontSize: '13px',
                                              },
                                            }}
                                          >
                                            {company?.CompanyName}
                                          </Typography>
                                          <Box
                                            style={{
                                              backgroundColor: company?.IsDonationMode ? "#29B3E2" : "var(--yellow)",
                                              color: "white",
                                              padding: window.innerWidth < 480 ? "1px 6px" : "2px 8px",
                                              borderRadius: "12px",
                                              fontSize: window.innerWidth < 480 ? "9px" : "11px",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              letterSpacing: "0.5px",
                                            }}
                                            sx={{
                                              '@media (max-width: 480px)': {
                                                padding: '1px 6px',
                                                fontSize: '9px',
                                              },
                                            }}
                                          >
                                            {company?.IsDonationMode ? "Donation" : "Job"}
                                          </Box>
                                        </Box>
                                        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                          <Typography
                                            style={{
                                              fontSize: window.innerWidth < 480 ? "11px" : "13px",
                                              fontWeight: "600",
                                              color: "#555",
                                            }}
                                            sx={{
                                              '@media (max-width: 480px)': {
                                                fontSize: '11px',
                                              },
                                            }}
                                          >
                                            {company?.Role}
                                          </Typography>
                                          <Typography
                                            style={{
                                              fontSize: window.innerWidth < 480 ? "11px" : "13px",
                                              color: "#777",
                                              fontWeight: "500",
                                            }}
                                            sx={{
                                              '@media (max-width: 480px)': {
                                                fontSize: '11px',
                                              },
                                            }}
                                          >
                                            {company?.Role === "Customer"
                                              ? `${company?.FirstName || "Customer"} ${
                                                  company?.LastName || ""
                                                }`
                                              : company?.Role === "Worker"
                                              ? `${company?.FirstName || "Worker"} ${
                                                  company?.LastName || ""
                                                }`
                                              : company?.Role === "Company"
                                              ? `${
                                                  company?.OwnerName ||
                                                  company?.CompanyName
                                                }`
                                              : company?.CompanyName}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    }
                                    style={{ margin: 0, width: "100%" }}
                                  />
                                </Box>
                              );
                            })
                            )}
                            </Box>
                          </Box>
                        </RadioGroup>
                      </Box>
                    </FormControl>
                  </Box>
                )}

                {/* Selected Role Display with Cancel Option - Show when role is selected */}
                {showRoleSelection && emailCheckData && selectedRole && (
                  <Box style={{ width: "100%", marginTop: "10px" ,marginBottom: "10px"}}>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        backgroundColor: userType === "donor" ? "#E9F8FC" : "#FFF5EC",
                        border: `1px solid ${userType === "donor" ? "#29B3E2" : "var(--amber)"}`,
                        borderRadius: "8px",
                      }}
                    >
                      <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <CheckCircleOutlineIcon
                          style={{
                            color: userType === "donor" ? "#29B3E2" : "var(--amber)",
                            fontSize: "20px",
                          }}
                        />
                        <Box>
                          <Typography
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#333",
                            }}
                          >
                            {(() => {
                              const selectedCompany = emailCheckData?.companies?.find(
                                (company) => (company.UserId || company.Id) === selectedRole
                              );
                              if (selectedCompany) {
                                return `${selectedCompany.CompanyName} (${selectedCompany.Role})`;
                              }
                              return "";
                            })()}
                          </Typography>
                          <Typography
                            style={{
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#666",
                              marginTop: "2px",
                            }}
                          >
                            {(() => {
                              const selectedCompany = emailCheckData?.companies?.find(
                                (company) => (company.UserId || company.Id) === selectedRole
                              );
                              if (selectedCompany) {
                                return selectedCompany.Role === "Company"
                                  ? selectedCompany?.OwnerName || selectedCompany?.CompanyName
                                  : `${selectedCompany?.FirstName || ""} ${selectedCompany?.LastName || ""}`.trim();
                              }
                              return "";
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        onClick={() => {
                          setSelectedRole("");
                          setAccountSearchQuery("");
                        }}
                        style={{
                          padding: "4px",
                          color: "#999",
                        }}
                        size="small"
                      >
                        <CancelIcon style={{ fontSize: "20px" }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {recaptchaError && !showRoleSelection && (
                  <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                    {recaptchaError}
                  </Typography>
                )}
                <FormGroup style={{ width: "100%",marginTop: "10px"  }}>
                  <OrangeButton
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      borderRadius: "4px",
                      height: "48px",
                      textTransform: "none",
                    }}
                    type="submit"
                    disabled={
                      isLoading ||
                      isCheckingEmail ||
                      (showRoleSelection && !selectedRole)
                      // JOB-3368 follow-up: do NOT disable on
                      // !recaptchaReady. With recaptcha still loading the
                      // button used to be inert, so clicking with an empty
                      // email did nothing -- formik never ran handleSubmit
                      // and "Email is required" never showed. Let the click
                      // through; formik will validate first, surface the
                      // inline error if the input is empty/invalid, and the
                      // onSubmit handler already shows a toast if recaptcha
                      // is genuinely not ready when a valid email is sent.
                    }
                    isDonationMode={userType === "donor"}
                    label={
                      isLoading
                        ? "Sending..."
                        : isCheckingEmail
                        ? "Checking Email..."
                        : showRoleSelection
                        ? "Send Reset Email"
                        : "Check Email"
                    }
                  />
                </FormGroup>
                {!showRoleSelection && recaptchaReady && (
                  <Typography
                    variant="caption"
                    display="block"
                    style={{
                      marginTop: "12px",
                      color: "#666",
                      fontSize: "11px",
                      textAlign: "center",
                    }}
                  >
                    This page is protected by reCAPTCHA and subject to Google's{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#666" }}
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#666" }}
                    >
                      Terms of Service
                    </a>
                    .
                  </Typography>
                )}
              </Grid>
            </form>
          </Grid>
          </Grid>
        </Grid>
        <Grid
          className="d-flex justify-content-center align-items-center img decreaseSizeHei"
          style={{ height: "100vh", padding: 0, margin: 0 }}
          size={{
            xs: 12,
            md: 6
          }}>
          <div
            className="firgitPasswordImg forgotImg_cdn "
            style={{ height: "100vh", width: "100%" }}
          ></div>
        </Grid>
      </Grid>
      <showToaster />
    </Grid>
  );
};

export default ForgotPassword;
